import { ControlType, IEntity, Property, PropertyType } from "../../../base/types";
import { FC, useEffect, useState } from "react";
import { IControlAttributes } from "../../../base/models";
import { Registry } from "../../../base/register";
import { useGetProperties } from "../../../base/hooks";
import {
    useGetProgStructureBySemesterProgramSessionTypeQuery,
    useGetSemesterRegistrationQuery,
    useLazyGetCoursesQuery,
} from "./properties/programStructureApi";
import { Course, ICourseType } from "./properties/IProgramStructure";
import { useGetCourseHistoryByStudentIdQuery } from "./properties/courseHistoryApi";
import { useAuth } from "../../../../modules/auth";
import { useCreateMutation, useDeleteMutation, } from "../../../services/data/tableDataApi";
import { IFieldValue } from "../../../services/models";
import { Spinner } from "../../../widgets";

export const AcademicRegistrationComponent: FC<IControlAttributes> = ({
    control,
    className,
    children,
    props,
    designerMode,
    ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });

    const academicSession: string | undefined = properties[Property.AcademicSession];
    const coursesType: string | undefined = properties[Property.coursesType];
    const semesterId: string | undefined = properties[Property.SemesterId];
    const programId: string | undefined = properties[Property.ProgramId];
    const section: string | undefined = properties[Property.Section];
    const { user, siteId } = useAuth();
    const { data: programStructure, isLoading: isLoadingProgramStructure } = useGetProgStructureBySemesterProgramSessionTypeQuery({
        semesterId: semesterId || '',
        programId: programId || '',
        academicSession: academicSession || '',
        coursesType: coursesType || '',
        section: section || '',
    });

    const { data: courseHistory, refetch: refetchCourseHistory, isLoading: isLoadingCourseHistory } = useGetCourseHistoryByStudentIdQuery({
        studentId: user?.contactId || ''
    });

    const { data, isLoading: isLoadingSemesterRegistration } = useGetSemesterRegistrationQuery({
        studentId: user?.contactId || '',
        academicSession: academicSession || '',
    });

    const [getCourse] = useLazyGetCoursesQuery();
    const [courses, setCourses] = useState<Course[]>([]);
    const [, setError] = useState<string>('');
    const [selectedCourses, setSelectedCourses] = useState<Set<string | undefined>>(new Set());
    const [insertRecord] = useCreateMutation();
    const [removeRecord] = useDeleteMutation();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCourses = async () => {
            if (!programStructure) return;

            const fetchedCourses: Course[] = await Promise.all(
                programStructure.map(async (item) => {
                    let courseDetails: Course = {
                        courseId: item.courseId || '',
                        name: "",
                        credits: 0,
                        electiveGroupCode: null,
                        description: null,
                    };

                    if (item.courseId) {
                        try {
                            const response = await getCourse({ courseId: item.courseId }).unwrap();
                            courseDetails = {
                                ...courseDetails,
                                name: response.name,
                                credits: response.credits,
                                electiveGroupCode: response.electiveGroupCode,
                                description: response.description,
                            };
                        } catch (error) {
                            console.error(`Failed to fetch course details for ${item.courseId}`, error);
                        }
                    }

                    return courseDetails;
                })
            );

            setCourses(fetchedCourses);
            setLoading(false);
        };

        fetchCourses();
    }, [programStructure, getCourse]);

    useEffect(() => {
        if (!isLoadingProgramStructure && !isLoadingCourseHistory && !isLoadingSemesterRegistration) {
            setLoading(false);
        }
    }, [isLoadingProgramStructure, isLoadingCourseHistory, isLoadingSemesterRegistration]);

    const courseType = (courseType?: number) => {
        switch (courseType) {
            case ICourseType.OpenElective:
                return "Open Elective";
            case ICourseType.Elective:
                return "Elective";
            case ICourseType.Major:
                return "Major";
            default:
                return courseType;
        }
    };

    const addCourseHistory = async (newCourseHistoryRecord: any) => {
        saveFormData(newCourseHistoryRecord)
            .then(() => {
                refetchCourseHistory();
            })
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setError(message);
            });
    };

    useEffect(() => {
        if (courseHistory) {
            const initialSelectedCourses = new Set(
                courseHistory
                    .filter(
                        (history) =>
                            history.academicSession === academicSession &&
                            history.semesterId === semesterId &&
                            history.programId === programId
                    )
                    .map((history) => history.courseId)
            );
            setSelectedCourses(initialSelectedCourses || '');
        }
    }, [courseHistory, academicSession, semesterId, programId]);


    const updateCourseHistory = async (courseHistoryId: string) => {
        try {
            removeFormData(courseHistoryId)
                .then(() => {
                    refetchCourseHistory();
                })
                .catch(err => {
                    const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                    setError(message);
                }).finally(() => refetchCourseHistory());
            refetchCourseHistory();
        } catch (error) {
            console.error("Error updating course history:", error);
        }
    };

    const saveFormData = (record: IEntity): Promise<IEntity> => {
        const fields: IFieldValue[] = Object.entries(record)
            .filter(([_, value]) => value !== undefined)
            .map(([field, value]) => ({ field, value, validate: true }));

        return insertRecord({ portalId: siteId || '', userId: user?.contactId, tableId: "mshied_coursehistory", data: fields, triggers: true })
            .unwrap()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setError(message);
                return Promise.reject(message);
            });
    };

    const removeFormData = (courseHistoryId: string): Promise<void> => {
        return removeRecord({ portalId: siteId || '', userId: user?.contactId, sysId: courseHistoryId, tableId: 'mshied_coursehistory', triggers: true })
            .unwrap()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setError(message);
                return Promise.reject(message);
            });
    };

    const handleCheckboxChange = async (course: Course) => {
        const updatedSelectedCourses = new Set(selectedCourses);
        const courseHistoryRecord = courseHistory?.find(
            (history) =>
                history.academicSession === academicSession &&
                history.semesterId === semesterId &&
                history.programId === programId &&
                history.courseId === course.courseId
        );

        if (updatedSelectedCourses.has(course.courseId)) {
            updatedSelectedCourses.delete(course.courseId);
            if (courseHistoryRecord) {
                try {
                    await updateCourseHistory(courseHistoryRecord.courseHistoryId || '');
                } catch (error) {
                    console.error("Error updating course history record:", error);
                }
            }
        } else {
            updatedSelectedCourses.add(course.courseId);
            if (!courseHistoryRecord) {
                try {
                    const academicPeriodId = programStructure?.find((item) => item.courseId === course.courseId)?.academicYear;
                    const courseType = programStructure?.find((item) => item.courseId === course.courseId)?.courseType;
                    if (!academicPeriodId || !data) {
                        console.error("Academic Period ID not found for course:", course.courseId);
                        return;
                    }

                    const newCourseHistoryRecord = {
                        mshied_name: course.name,
                        "slcm_Program@odata.bind": `/mshied_programs(${programId})`,
                        "mshied_StudentId@odata.bind": `/contacts(${user?.contactId})`,
                        "edtec365_Semester@odata.bind": `/slcm_semesters(${semesterId})`,
                        mshied_creditsattempted: course.credits,
                        edtec365_semesterregistration: data[0]?.registrationId,
                        edtec365_registrationtype: 365970001,
                        "mshied_CourseId@odata.bind": `/mshied_courses(${course.courseId})`,
                        "mshied_AcademicPeriodid@odata.bind": `/mshied_programrequirements(${academicSession})`,
                        "edtec365_AcademicYear@odata.bind": `/mshied_programrequirements(${academicPeriodId})`,
                        edtec365_coursetype: courseType
                    };

                    await addCourseHistory(newCourseHistoryRecord);
                } catch (error) {
                    console.error("Error adding course to course history:", error);
                }
            }
        }

        setSelectedCourses(updatedSelectedCourses);
        refetchCourseHistory();
    };

    if (loading) {
        return <Spinner message="please wait..." show={loading} />;
    }

    return (
        <div className="container mt-4">
            {programStructure?.map((item) => {
                const course = courses.find((c) => c.courseId === item.courseId);

                return (
                    <div
                        className="row border rounded p-3 mb-3 align-items-center"
                        key={item.programRequirementId}
                    >
                        <div className="col-2 d-flex justify-content-center">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={item.courseType === ICourseType.Major ? true : selectedCourses.has(course?.courseId || '') || false}
                                disabled={item.courseType === ICourseType.Major ? true : false}
                                onChange={() => handleCheckboxChange(course!)}
                            />
                        </div>
                        <div className="col-4">
                            <span>Course</span> <br />
                            {course?.name || "Course not found"}
                        </div>
                        <div className="col-3">
                            <span>Course Type</span> <br />
                            {courseType(item.courseType)}
                        </div>
                        <div className="col-3">
                            <span>Credits</span> <br />
                            {course?.credits || 0}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};


Registry.addControl({
    id: "b813ca86-c2ad-4338-8b34-61649e869b88",
    name: "Academic Registration",
    type: ControlType.AcademicRegistrationComponent,
    properties: [
        { id: Property.Tooltip, name: "Tooltip", type: PropertyType.String },
        { id: Property.ProgramId, name: "ProgramId", type: PropertyType.String },
        { id: Property.SemesterId, name: "SemesterId", type: PropertyType.String },
        { id: Property.AcademicSession, name: "AcademicSession", type: PropertyType.String },
        { id: Property.coursesType, name: "Course Type", type: PropertyType.String },
        { id: Property.Section, name: "Section", type: PropertyType.String },
    ],
    component: AcademicRegistrationComponent,
});
